<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const { accountScopedRoute } = useAccount();

const templates = useMapGetter('accountTemplates/getAccountTemplates');
const uiFlags = useMapGetter('accountTemplates/getUIFlags');

const showDeleteConfirmationPopup = ref(false);
const activeTemplate = ref(null);

onMounted(() => {
  store.dispatch('accountTemplates/fetchAccountTemplates');
});

const tableHeaders = computed(() => [
  t('SUPER_ADMIN.TEMPLATES.TABLE.NAME'),
  t('SUPER_ADMIN.TEMPLATES.TABLE.DESCRIPTION'),
  t('SUPER_ADMIN.TEMPLATES.TABLE.ACTIONS'),
]);

const openDeletePopup = template => {
  activeTemplate.value = template;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
  activeTemplate.value = null;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch(
      'accountTemplates/deleteAccountTemplate',
      activeTemplate.value.id
    );
    useAlert(t('SUPER_ADMIN.TEMPLATES.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('SUPER_ADMIN.TEMPLATES.DELETE.ERROR'));
  } finally {
    closeDeletePopup();
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="t('SUPER_ADMIN.TEMPLATES.LOADING')"
    :no-records-found="!templates.length"
    :no-records-message="t('SUPER_ADMIN.TEMPLATES.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('SUPER_ADMIN.TEMPLATES.HEADER')"
        :description="t('SUPER_ADMIN.TEMPLATES.DESCRIPTION')"
      >
        <template #actions>
          <router-link :to="accountScopedRoute('super_admin_template_new')">
            <Button
              :label="t('SUPER_ADMIN.TEMPLATES.NEW_TEMPLATE')"
              size="sm"
            />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="templates"
        :no-data-message="t('SUPER_ADMIN.TEMPLATES.NO_RECORDS')"
      >
        <template #row="{ items }">
          <BaseTableRow
            v-for="template in items"
            :key="template.id"
            :item="template"
          >
            <template #default>
              <BaseTableCell>{{ template.name }}</BaseTableCell>
              <BaseTableCell>{{ template.description }}</BaseTableCell>
              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <router-link
                    :to="
                      accountScopedRoute('super_admin_template_edit', {
                        templateId: template.id,
                      })
                    "
                  >
                    <Button
                      v-tooltip.top="t('SUPER_ADMIN.TEMPLATES.EDIT')"
                      icon="i-woot-edit-pen"
                      slate
                      sm
                    />
                  </router-link>
                  <Button
                    v-tooltip.top="t('SUPER_ADMIN.TEMPLATES.DELETE.BUTTON')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDeletePopup(template)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="t('SUPER_ADMIN.TEMPLATES.DELETE.CONFIRM_TITLE')"
      :message="t('SUPER_ADMIN.TEMPLATES.DELETE.CONFIRM_MESSAGE')"
      :message-value="activeTemplate ? ` ${activeTemplate.name} ?` : ''"
      :confirm-text="t('SUPER_ADMIN.TEMPLATES.DELETE.CONFIRM_YES')"
      :reject-text="t('SUPER_ADMIN.TEMPLATES.DELETE.CONFIRM_NO')"
    />
  </SettingsLayout>
</template>
